import './Login.css';
import { useState, FormEvent, ChangeEvent, KeyboardEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LuSend } from 'react-icons/lu';
import { FaArrowRight } from 'react-icons/fa';
import { HiOutlineMail } from 'react-icons/hi';
import { RiTelegram2Line } from 'react-icons/ri';
import { useAuthStore } from '../../stores';
import { sendVerificationCode, verifyCodeAndLogin } from '../../services/authService';
import { setUserID, setUserProperties, trackEvent } from '../../services/analyticsService';
import { trackAuthEvent } from '../../utils/posthogUtils';
import { handleOTPInputChange } from '../../utils/otpUtils';
import { validateBusinessEmail } from '../../utils/emailValidation';

const atlasReverseLogo = require('../../assets/logos/atlas-lockup-reverse.png');

function Login() {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);

  const [step, setStep] = useState<1 | 2>(1);
  const [email, setEmail] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState<string[]>(['', '', '', '', '', '']);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [resendCooldown, setResendCooldown] = useState<number>(0);

  // Countdown timer for OTP expiration (show minutes only)
  useEffect(() => {
    if (!expiresAt) return;

    const updateCountdown = () => {
      const now = new Date();
      const expireTime = new Date(expiresAt);
      const diffMs = expireTime.getTime() - now.getTime();

      if (diffMs <= 0) {
        setTimeRemaining('Code expired');
        return;
      }

      const minutes = Math.ceil(diffMs / 60000);
      setTimeRemaining(`Valid for ${minutes} min${minutes !== 1 ? 's' : ''}`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [expiresAt]);

  // Resend code cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setTimeout(() => {
      setResendCooldown(resendCooldown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleEmailSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    // Validate business email first
    const emailValidation = validateBusinessEmail(email);
    if (!emailValidation.isValid) {
      setError(emailValidation.error || 'Invalid email address');
      return;
    }

    setLoading(true);

    const result = await sendVerificationCode(email);

    if (result.success) {
      trackAuthEvent.verificationCodeSent(email);
      setStep(2);
      if (result.data?.expiresAt) {
        setExpiresAt(result.data.expiresAt);
      }
    } else {
      const msg = result.message || '';
      if (msg.toLowerCase().includes('not registered') || msg.toLowerCase().includes('not found') || msg.toLowerCase().includes('not exist')) {
        setError('User not found. Please contact jpande@gatekeepersystems.com to get access to Atlas.');
      } else {
        setError(msg || 'Failed to send verification code');
      }
    }

    setLoading(false);
  };

  const handleVerificationSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const code = verificationCode.join('');

    if (code.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    const result = await verifyCodeAndLogin(email, code);

    if (result.success && result.token && result.user) {
      trackAuthEvent.verificationCodeSubmitted(true);
      login(result.token, result.user);

      const userId = (result.user as any)?.id || email;
      console.log('User ID:', userId);

      const userPropsForPostHog = {
        email: (result.user as any)?.email || email,
        name: (result.user as any)?.name,
        role: (result.user as any)?.role,
      };
      setUserID(userId, userPropsForPostHog);

      setUserProperties({
        'user_email_domain': email.split('@')[1],
        'login_method': 'email_verification'
      });

      trackEvent('user_login', {
        'user_id': userId,
        'login_method': 'email_verification',
        'success': true
      });

      trackAuthEvent.userLoggedIn(userId, email);

      navigate('/dashboard');
    } else {
      setError(result.message || 'Invalid verification code');

      trackAuthEvent.verificationCodeSubmitted(false);
      trackEvent('login_failed', {
        'reason': 'invalid_code'
      });

      setLoading(false);
    }
  };

  const handleCodeChange = (index: number, value: string) => {
    // Only allow numeric characters
    const numericValue = value.replace(/\D/g, '');
    const { newCode, nextFocusIndex } = handleOTPInputChange(index, numericValue, verificationCode);
    setVerificationCode(newCode);

    if (nextFocusIndex !== null) {
      setTimeout(() => {
        document.getElementById(`code-${nextFocusIndex}`)?.focus();
      }, 0);
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      document.getElementById(`code-${index - 1}`)?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const digits = pastedText.replace(/\D/g, '').slice(0, 6).split('');
    const newCode = ['', '', '', '', '', ''];

    for (let i = 0; i < digits.length && i < 6; i++) {
      newCode[i] = digits[i];
    }

    setVerificationCode(newCode);

    // Focus the last filled field
    const lastFilledIndex = Math.min(digits.length - 1, 5);
    setTimeout(() => {
      document.getElementById(`code-${lastFilledIndex}`)?.focus();
    }, 0);
  };

  const handleChangeEmail = () => {
    setError('');
    setStep(1);
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError('');

    const result = await sendVerificationCode(email);

    if (result.success) {
      trackAuthEvent.verificationCodeResent(email);
      setVerificationCode(['', '', '', '', '', '']);
      if (result.data?.expiresAt) {
        setExpiresAt(result.data.expiresAt);
      }
      setResendCooldown(120);
    } else {
      setError(result.message || 'Failed to resend verification code');
    }

    setLoading(false);
  };

  return (
    <div className="login-container">
      <div className="login-form-container">
        <div className="login-left">
          <img src={atlasReverseLogo} alt="Atlas" className="login-logo" />
          <p className="login-subtitle">Enterprise Retail Analytics & Opportunity Management Platform</p>

          <ul className="login-features">
            <li>AI-Powered Opportunity Detection</li>
            <li>Real-Time Collaboration & Analytics</li>
            <li>Advanced Approval Workflows</li>
          </ul>
        </div>

        <div className="login-right">
          <div className="login-card">
            <div className='login-header'>
              <h2 className="login-card-title">Welcome Back</h2>
              <p className="login-card-subtitle" key={`subtitle-${step}`}>
                {step === 1 ? 'Sign in to access your dashboard' : 'Enter the verification code sent to your email'}
              </p>

              {error ? (
                <div className="error-banner form-animate-in" role="alert" aria-live="assertive">
                  <span className="error-icon" aria-hidden="true">⚠</span>
                  <span className="error-text">{error}</span>
                </div>
              ) : step === 2 ? (
                <div className="info-banner form-animate-in" role="status" aria-live="polite">
                  <span className="info-icon" aria-hidden="true">ⓘ</span>
                  <span className="info-text">
                    Verification code sent to <span className="email-display-text">{email}</span>
                    {timeRemaining && <span className="expiration-timer"> ({timeRemaining})</span>}
                  </span>
                </div>
              ) : null}

              {step === 1 ? (
                <form className="login-form form-animate-in" onSubmit={handleEmailSubmit} key="email-form">
                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <div className="input-wrapper">
                      <HiOutlineMail className="input-icon-svg" aria-hidden="true" />
                      <input
                        type="email"
                        id="email"
                        placeholder="Enter your email address"
                        className="form-input"
                        value={email}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                        aria-label="Email address"
                        aria-required="true"
                        required
                      />
                    </div>
                  </div>

                  {/* Email Verification Hint Box */}
                  <div className="email-verification-hint">
                    <div className="hint-icon-wrapper">
                      <RiTelegram2Line className="hint-icon" />
                    </div>
                    <div className="hint-content">
                      <h3 className="hint-title">Email Verification</h3>
                      <p className="hint-text">A 6-digit verification code will be sent to your email. Delivery may take 1–2 minutes.</p>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={loading}
                    aria-busy={loading}
                    aria-label="Send verification code"
                  >
                    {loading ? 'Sending...' : 'Send Verification Code'}
                    {!loading && <LuSend className="btn-icon-svg" aria-hidden="true" />}
                  </button>
                </form>
              ) : (
                <form className="login-form form-animate-in" onSubmit={handleVerificationSubmit} key="verification-form">
                  <div className="form-group">
                    <label htmlFor="email-display">Email Address</label>
                    <div className="input-wrapper email-display">
                      <HiOutlineMail className="input-icon-svg" aria-hidden="true" />
                      <input
                        type="email"
                        id="email-display"
                        className="form-input"
                        value={email}
                        disabled
                        aria-label="Email address (read-only)"
                      />
                      <button
                        type="button"
                        className="change-btn"
                        onClick={handleChangeEmail}
                        aria-label="Change email address"
                      >
                        Change
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Verification Code</label>
                    <div className="verification-inputs" role="group" aria-label="6-digit verification code input" onPaste={handlePaste}>
                      {verificationCode.map((digit, index) => (
                        <input
                          key={index}
                          id={`code-${index}`}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          className="code-input"
                          value={digit}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => handleCodeChange(index, e.target.value)}
                          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleKeyDown(index, e)}
                          aria-label={`Verification code digit ${index + 1}`}
                          aria-required="true"
                        />
                      ))}
                    </div>
                    <button
                      type="button"
                      className="resend-btn"
                      onClick={handleResendCode}
                      disabled={resendCooldown > 0 || loading}
                      aria-label={resendCooldown > 0 ? `Resend code in ${resendCooldown} seconds` : 'Resend verification code'}
                    >
                      {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                    </button>
                  </div>

                  <div className="form-group checkbox-group">
                    <input type="checkbox" id="keep-signed" />
                    <label htmlFor="keep-signed">Keep me signed in for 30 days</label>
                  </div>

                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={loading}
                    aria-busy={loading}
                    aria-label="Verify code and sign in"
                  >
                    {loading ? 'Verifying...' : 'Verify & Sign In'}
                    {!loading && <FaArrowRight className="btn-icon-svg" aria-hidden="true" />}
                  </button>
                </form>
              )}
            </div>

            <div className="login-footer">
              <span className="security-badge">🛡</span>
              <span className="security-text">Enterprise-grade security</span>
              <p className="copyright">© 2026 Atlas</p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default Login;
